import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { Badge } from "../components/Badge";
import { SchemaPrint } from "../components/SchemaPrint";
import { SignaturePad } from "../components/SignaturePad";
import { PhotoCapture } from "../components/PhotoCapture";
import { SCHEMAS, GFM_SITES, permitMeta } from "../lib/permitSchemas";
import logo from "../assets/gfm-logo-square.png";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, FilePlus2, Printer, IdCard, Upload, FileText, Trash2, Loader2, BadgeCheck, CalendarX, RefreshCw, AlertTriangle, History, Trash, Plus, Pencil } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (dt) => { if (!dt) return "—"; try { return new Date(dt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return dt; } };
const fmtDay = (dt) => { if (!dt) return ""; try { return new Date(dt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); } catch { return dt; } };
const isExpired = (dt) => dt && new Date(dt) < new Date();
const daysUntil = (dt) => (dt ? Math.ceil((new Date(dt) - new Date()) / 86400000) : null);

export default function ContractorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [permits, setPermits] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [printId, setPrintId] = useState(null);
  const [printBadge, setPrintBadge] = useState(null); // site-scoped badge object
  const fileRef = useRef(null);
  // set photo
  const [photoOpen, setPhotoOpen] = useState(false);
  const [newPhoto, setNewPhoto] = useState(null);
  const [savingPhoto, setSavingPhoto] = useState(false);
  // edit details
  const [editOpen, setEditOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  // renew
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewInd, setRenewInd] = useState(null);
  const [renewing, setRenewing] = useState(false);
  const [renewDate, setRenewDate] = useState("");
  const [renewSig1, setRenewSig1] = useState(null);
  const [renewSig2, setRenewSig2] = useState(null);
  // add induction handled on its own page (/contractors/:id/induct)
  const [delIndOpen, setDelIndOpen] = useState(false);
  const [delInd, setDelInd] = useState(null);
  // delete
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    const [cr, pr] = await Promise.all([
      api.get(`/contractors/${id}`),
      api.get("/permits", { params: { contractor_id: id } }),
    ]);
    setC(cr.data);
    setPermits(pr.data);
  };
  useEffect(() => { load().catch(() => navigate("/")); }, [id]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/contractors/${id}/rams`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("RAMS uploaded");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const deleteRam = async (ramId) => { await api.delete(`/contractors/${id}/rams/${ramId}`); toast.success("RAMS removed"); load(); };
  const setRamExpiry = async (ramId, value) => {
    setC((prev) => ({ ...prev, rams: prev.rams.map((r) => (r.id === ramId ? { ...r, expiry_date: value || null } : r)) }));
    try { await api.put(`/contractors/${id}/rams/${ramId}`, { expiry_date: value || null }); }
    catch { toast.error("Could not save expiry date"); load(); }
  };

  const nowLocal = () => { const n = new Date(); n.setMinutes(n.getMinutes() - n.getTimezoneOffset()); return n.toISOString().slice(0, 16); };
  const openRenew = (ind) => { setRenewInd(ind); setRenewDate(nowLocal()); setRenewSig1(null); setRenewSig2(null); setRenewOpen(true); };
  const doRenew = async () => {
    if (!renewDate) return toast.error("Set the new induction date.");
    setRenewing(true);
    try {
      const payload = { induction_date: new Date(renewDate).toISOString() };
      if (renewSig1) payload.inductor_signature = renewSig1;
      if (renewSig2) payload.inductee_signature = renewSig2;
      const { data } = await api.post(`/contractors/${id}/inductions/${renewInd.id}/renew`, payload);
      setC(data); setRenewOpen(false);
      toast.success("Induction renewed — valid for another 3 years");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setRenewing(false); }
  };

  const openAdd = () => navigate(`/contractors/${id}/induct`);
  const doDeleteInduction = async () => {
    try {
      const { data } = await api.delete(`/contractors/${id}/inductions/${delInd.id}`);
      setC(data);
      setDelIndOpen(false);
      toast.success("Site induction removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try { await api.delete(`/contractors/${id}`); toast.success("Contractor and all their data removed"); navigate("/"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); setDeleting(false); }
  };

  const doPrint = (indId) => { setPrintBadge(null); setPrintId(indId); setTimeout(() => window.print(), 100); };

  const printSiteBadge = (ind) => {
    const active = permits.filter((p) => !isExpired(p.expiry_at) && !p.cancelled_at && p.site_location === ind.site).flatMap((p) => p.permit_types || []);
    setPrintBadge({
      contractor_id: c.id, contractor_name: c.name, company: c.company, id_number: c.dbs_number,
      contractor_photo: c.contractor_photo, site: ind.site, inducted_at: ind.induction_date,
      permit_types: Array.from(new Set(active)), issue_at: ind.induction_date, expiry_at: ind.induction_expiry,
    });
    setPrintId(null);
    setTimeout(() => window.print(), 100);
  };

  const printAllBadge = () => {
    const activeTypes = Array.from(new Set(permits.filter((p) => !isExpired(p.expiry_at) && !p.cancelled_at).flatMap((p) => p.permit_types || [])));
    const latestExpiry = (c.inductions || []).filter((i) => !isExpired(i.induction_expiry)).map((i) => i.induction_expiry).sort().slice(-1)[0];
    setPrintBadge({
      contractor_id: c.id, contractor_name: c.name, company: c.company, id_number: c.dbs_number,
      contractor_photo: c.contractor_photo, permit_types: activeTypes,
      issue_at: (c.inductions || [])[0]?.induction_date, inducted_at: (c.inductions || [])[0]?.induction_date, expiry_at: latestExpiry,
    });
    setPrintId(null);
    setTimeout(() => window.print(), 100);
  };

  const openEdit = () => {
    setEditForm({
      first_name: c.first_name || "", last_name: c.last_name || "", company: c.company || "",
      email: c.email || "",
      dbs_number: c.dbs_number || "", mobile: c.mobile || "", emergency_contact: c.emergency_contact || "", notes: c.notes || "",
    });
    setEditOpen(true);
  };
  const doEditSave = async () => {
    if (!editForm.first_name || !editForm.last_name) return toast.error("First and last name are required.");
    if (!editForm.company) return toast.error("Company is required.");
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/contractors/${id}`, editForm);
      setC(data); setEditOpen(false);
      toast.success("Details updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSavingEdit(false); }
  };

  const savePhoto = async () => {
    setSavingPhoto(true);
    try {
      const { data } = await api.put(`/contractors/${id}`, { contractor_photo: newPhoto });
      setC(data); setPhotoOpen(false);
      toast.success("Photo updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSavingPhoto(false); }
  };

  if (!c) return <AppShell><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div></AppShell>;

  const inductions = c.inductions || [];
  const activeTypes = Array.from(new Set(permits.filter((p) => !isExpired(p.expiry_at) && !p.cancelled_at).flatMap((p) => p.permit_types || [])));
  const badge = { contractor_id: c.id, contractor_name: c.name, company: c.company, id_number: c.dbs_number, contractor_photo: c.contractor_photo, permit_types: activeTypes, inducted_at: inductions[0]?.induction_date, issue_at: inductions[0]?.induction_date, expiry_at: inductions.filter((i) => !isExpired(i.induction_expiry)).map((i) => i.induction_expiry).sort().slice(-1)[0] };
  const printInd = inductions.find((i) => i.id === printId) || inductions[0];

  return (
    <>
      <div className="print-hidden">
        <AppShell>
          <button onClick={() => navigate("/")} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-brand" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" /> Back to contractors
          </button>

          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 overflow-hidden rounded-md border border-border bg-neutral-100">
                {c.contractor_photo ? <img src={c.contractor_photo} alt={c.name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-xs text-neutral-400">No photo</div>}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">{c.reference}</div>
                <h1 className="font-heading text-3xl font-bold tracking-tight">{c.name}</h1>
                <p className="text-muted-foreground">{c.company} · DBS {c.dbs_number || "—"}</p>
                <p className="text-xs text-muted-foreground">Mobile: {c.mobile || "—"} · Emergency: {c.emergency_contact || "—"}{c.notes ? ` · Medical: ${c.notes}` : ""}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={openEdit} data-testid="edit-details-btn">
                <Pencil className="mr-2 h-4 w-4" /> Edit details
              </Button>
              <Button variant="outline" onClick={() => { setNewPhoto(c.contractor_photo || null); setPhotoOpen(true); }} data-testid="set-photo-btn">
                <IdCard className="mr-2 h-4 w-4" /> {c.contractor_photo ? "Change photo" : "Add photo"}
              </Button>
              <Button variant="outline" onClick={printAllBadge} data-testid="print-badge-btn">
                <IdCard className="mr-2 h-4 w-4" /> Print badge
              </Button>
              <Button className="bg-brand hover:bg-brand-dark" onClick={() => navigate(`/contractors/${id}/issue`)} data-testid="issue-permit-button">
                <FilePlus2 className="mr-2 h-4 w-4" /> Issue permit
              </Button>
              <Button variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/5" onClick={() => setDeleteOpen(true)} data-testid="delete-contractor-btn">
                <Trash className="mr-2 h-4 w-4" /> Remove
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-4"><div className="sticky top-24"><Badge permit={badge} /></div></div>

            <div className="space-y-6 lg:col-span-8">
              {/* Site inductions */}
              <div className="rounded-md border border-border bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold tracking-tight">Site inductions</h3>
                  <Button size="sm" variant="outline" onClick={openAdd} data-testid="add-induction-btn"><Plus className="mr-2 h-4 w-4" /> Add site</Button>
                </div>
                <div className="space-y-3">
                  {inductions.map((ind) => {
                    const exp = isExpired(ind.induction_expiry);
                    return (
                      <div key={ind.id} className="rounded-md border border-border p-4" data-testid={`induction-${ind.id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="font-semibold">{ind.site}</div>
                            <div className="mt-1">
                              {exp ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700" data-testid={`induction-status-${ind.id}`}><CalendarX className="h-3 w-3" /> Expired {fmtDay(ind.induction_expiry)}</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700" data-testid={`induction-status-${ind.id}`}><BadgeCheck className="h-3 w-3" /> Valid to {fmtDay(ind.induction_expiry)}</span>
                              )}
                              {ind.history?.length > 0 && <span className="ml-2 text-xs text-muted-foreground">{ind.history.length} renewal(s)</span>}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant={exp ? "default" : "outline"} className={exp ? "bg-brand hover:bg-brand-dark" : ""} onClick={() => openRenew(ind)} data-testid={`renew-induction-btn-${ind.id}`}><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Renew</Button>
                            <Button size="sm" variant="outline" onClick={() => doPrint(ind.id)} data-testid={`print-induction-btn-${ind.id}`}><Printer className="mr-1.5 h-3.5 w-3.5" /> Print</Button>
                            <Button size="sm" variant="outline" onClick={() => printSiteBadge(ind)} data-testid={`print-site-badge-btn-${ind.id}`}><IdCard className="mr-1.5 h-3.5 w-3.5" /> Badge</Button>
                            <Button size="sm" variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/5" onClick={() => { setDelInd(ind); setDelIndOpen(true); }} data-testid={`remove-induction-btn-${ind.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </div>
                        {ind.history?.length > 0 && (
                          <div className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground" data-testid={`induction-history-${ind.id}`}>
                            {[...ind.history].reverse().map((h, i) => (
                              <div key={i}>Renewed {fmtDay(h.renewed_at)} by {h.renewed_by} — previous valid to {fmtDay(h.induction_expiry)}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* RAMS */}
              <div className="rounded-md border border-border bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold tracking-tight">RAMS documents</h3>
                  <input ref={fileRef} type="file" className="hidden" onChange={onUpload} data-testid="rams-file-input" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.csv,.txt" />
                  <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="rams-upload-btn">
                    {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Upload
                  </Button>
                </div>
                {(!c.rams || c.rams.length === 0) ? (
                  <div className="rounded-md border border-dashed border-border py-8 text-center text-sm text-muted-foreground" data-testid="rams-empty">No RAMS uploaded yet.</div>
                ) : (
                  <ul className="divide-y divide-border rounded-md border border-border">
                    {c.rams.map((r) => <RamsRow key={r.id} r={r} cid={id} onExpiry={setRamExpiry} onDelete={deleteRam} />)}
                  </ul>
                )}
              </div>

              {/* Issued permits */}
              <div className="rounded-md border border-border bg-white p-6 shadow-sm">
                <h3 className="mb-4 font-heading text-lg font-bold tracking-tight">Issued permits</h3>
                {permits.length === 0 ? (
                  <div className="rounded-md border border-dashed border-border py-8 text-center text-sm text-muted-foreground" data-testid="permits-empty">No permits issued yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <tr><th className="px-3 py-2">Permit No.</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Site</th><th className="px-3 py-2">Expiry</th><th className="px-3 py-2">Status</th></tr>
                      </thead>
                      <tbody>
                        {permits.map((p) => (
                          <tr key={p.id} onClick={() => navigate(`/permits/${p.id}`)} data-testid={`permit-row-${p.id}`} className="cursor-pointer border-t border-border hover:bg-secondary/50">
                            <td className="px-3 py-2 font-semibold text-brand">{p.permit_number}</td>
                            <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{(p.permit_types || []).map((t) => <span key={t} className="rounded px-1.5 py-0.5 text-[10px] font-semibold text-white" style={{ background: permitMeta(t)?.color }}>{permitMeta(t)?.short}</span>)}</div></td>
                            <td className="px-3 py-2 text-muted-foreground">{p.site_location}</td>
                            <td className="px-3 py-2 text-muted-foreground">{fmt(p.expiry_at)}</td>
                            <td className="px-3 py-2">{p.cancelled_at ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Cancelled</span> : p.clearance ? <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">Closed</span> : isExpired(p.expiry_at) ? <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-500">Expired</span> : <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">Active</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </AppShell>
      </div>

      {/* Renew dialog */}
      <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
        <DialogContent className="max-w-lg" data-testid="renew-dialog">
          <DialogHeader><DialogTitle className="font-heading">Renew induction — {renewInd?.site}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Runs a fresh induction and resets the 3-year validity for this site.</p>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>New induction date & time</Label><Input type="datetime-local" data-testid="renew-date" value={renewDate} onChange={(e) => setRenewDate(e.target.value)} /></div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SignaturePad label="Inductor signature (optional)" testid="renew-sig-inductor" value={renewSig1} onChange={setRenewSig1} />
              <SignaturePad label="Inductee signature (optional)" testid="renew-sig-inductee" value={renewSig2} onChange={setRenewSig2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewOpen(false)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-dark" onClick={doRenew} disabled={renewing} data-testid="confirm-renew-btn">{renewing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm renewal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add induction dialog */}
      <Dialog open={delIndOpen} onOpenChange={setDelIndOpen}>
        <DialogContent className="max-w-md" data-testid="remove-induction-dialog">
          <DialogHeader><DialogTitle className="font-heading">Remove site induction</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Remove the induction for <span className="font-semibold text-foreground">{delInd?.site}</span>? This deletes it and its renewal history for this contractor.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelIndOpen(false)}>Keep it</Button>
            <Button className="bg-destructive text-white hover:bg-destructive/90" onClick={doDeleteInduction} data-testid="confirm-remove-induction-btn">Remove induction</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit details dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg" data-testid="edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit contractor details</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5"><Label>First name</Label><Input data-testid="e-first" value={editForm.first_name || ""} onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Last name</Label><Input data-testid="e-last" value={editForm.last_name || ""} onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Company</Label><Input data-testid="e-company" value={editForm.company || ""} onChange={(e) => setEditForm((f) => ({ ...f, company: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="e-email" type="email" value={editForm.email || ""} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>DBS Certificate number</Label><Input data-testid="e-dbs" value={editForm.dbs_number || ""} onChange={(e) => setEditForm((f) => ({ ...f, dbs_number: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Mobile number</Label><Input data-testid="e-mobile" value={editForm.mobile || ""} onChange={(e) => setEditForm((f) => ({ ...f, mobile: e.target.value }))} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Emergency contact</Label><Input data-testid="e-emergency" value={editForm.emergency_contact || ""} onChange={(e) => setEditForm((f) => ({ ...f, emergency_contact: e.target.value }))} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Notes / medical conditions</Label><Textarea data-testid="e-notes" rows={2} value={editForm.notes || ""} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-dark" onClick={doEditSave} disabled={savingEdit} data-testid="save-edit-btn">{savingEdit && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set photo dialog */}
      <Dialog open={photoOpen} onOpenChange={setPhotoOpen}>
        <DialogContent className="max-w-sm" data-testid="photo-dialog">
          <DialogHeader><DialogTitle className="font-heading">Contractor photo</DialogTitle></DialogHeader>
          <PhotoCapture value={newPhoto} onCapture={setNewPhoto} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhotoOpen(false)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-dark" onClick={savePhoto} disabled={savingPhoto} data-testid="save-photo-btn">{savingPhoto && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save photo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md" data-testid="delete-dialog">
          <DialogHeader><DialogTitle className="font-heading">Remove contractor</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This permanently deletes <span className="font-semibold text-foreground">{c.name}</span> along with all inductions, RAMS and <span className="font-semibold text-foreground">{permits.length} issued permit(s)</span>. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Keep contractor</Button>
            <Button className="bg-destructive text-white hover:bg-destructive/90" onClick={doDelete} disabled={deleting} data-testid="confirm-delete-btn">{deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Delete all data</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print area */}
      <div className="print-only">
        {printBadge ? (
          <div className="p-8"><Badge permit={printBadge} /></div>
        ) : printInd && (
          <div className="a4-landscape induction-print bg-white">
            <style>{`@media print { @page { size: A4 landscape; margin: 7mm; } }`}</style>
            <div className="mb-1 border-b-2 border-brand pb-1">
              <div className="flex items-start justify-between">
                <div className="leading-tight text-neutral-600">Level 3 : Quality Operations Procedures · Type : GFM Compliance Policy Manual — Section 15 · Name : Site Induction · Site : {printInd.site}</div>
                <img src={logo} alt="GFM" className="h-11 w-11 object-contain" />
              </div>
              <div className="mt-1 flex items-end justify-between">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-brand">GFM Site or Activity Induction</div>
                  <h1 className="font-heading text-4xl font-extrabold uppercase leading-none tracking-tight text-brand">{c.name}</h1>
                  <div className="text-xl font-bold text-neutral-700">{c.company}</div>
                </div>
                <div className="text-right"><div className="uppercase tracking-wider text-neutral-500">Reference</div><div className="font-heading font-bold text-brand">{c.reference}</div></div>
              </div>
              <div className="mt-2 grid grid-cols-4 gap-x-6 gap-y-0.5">
                <div><b>DBS No:</b> {c.dbs_number || "—"}</div>
                <div><b>Site:</b> {printInd.site}</div>
                <div><b>Induction date:</b> {fmt(printInd.induction_date)}</div>
                <div><b>Valid until:</b> {fmt(printInd.induction_expiry)}</div>
              </div>
            </div>
            <div className="ind-cols">
              <SchemaPrint schema={SCHEMAS.site_induction} data={printInd.induction_data || {}} />
            </div>
            <div className="no-break mt-1 grid grid-cols-2 gap-6 border-t-2 border-brand pt-1">
              <SigBlock title="GFM Inductor" name={printInd.inductor_name} sub={printInd.inductor_job_title} sig={printInd.inductor_signature} date={printInd.induction_date} />
              <SigBlock title="Inductee" name={c.name} sig={printInd.inductee_signature} date={printInd.induction_date} />
              <div className="col-span-2 text-center italic text-neutral-600">This induction process must be reviewed every 3 years, or when there is significant or material change.</div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function RamsRow({ r, cid, onExpiry, onDelete }) {
  const days = daysUntil(r.expiry_date);
  const out = r.expiry_date && days < 0;
  const soon = r.expiry_date && days >= 0 && days <= 30;
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`rams-item-${r.id}`}>
      <a href={`${BACKEND_URL}/api/contractors/${cid}/rams/${r.id}/download`} target="_blank" rel="noreferrer" className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium text-brand hover:underline">
        <FileText className="h-4 w-4 flex-shrink-0" /><span className="truncate">{r.original_filename}</span>
      </a>
      <div className="flex items-center gap-3">
        {out && <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700" data-testid={`rams-flag-expired-${r.id}`}><AlertTriangle className="h-3 w-3" /> Out of date</span>}
        {soon && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700" data-testid={`rams-flag-soon-${r.id}`}>Due in {days}d</span>}
        <div className="flex items-center gap-1"><Label className="text-xs text-muted-foreground">Review by</Label><Input type="date" value={r.expiry_date ? r.expiry_date.slice(0, 10) : ""} onChange={(e) => onExpiry(r.id, e.target.value)} data-testid={`rams-expiry-${r.id}`} className={`h-8 w-36 text-xs ${out ? "border-red-300" : ""}`} /></div>
        <span className="whitespace-nowrap text-xs text-muted-foreground">{(r.size / 1024).toFixed(0)} KB</span>
        <button onClick={() => onDelete(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`rams-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </li>
  );
}

function SigBlock({ title, name, sub, sig, date }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-brand">{title}</div>
      <div className="mt-1 h-14 border-b border-neutral-400">{sig && <img src={sig} alt="signature" className="h-14 object-contain" />}</div>
      <div className="mt-1 text-[12px] font-semibold">{name || "—"}</div>
      {sub && <div className="text-[10px] text-neutral-500">{sub}</div>}
      <div className="text-[10px] text-neutral-500">Date: {fmt(date)}</div>
    </div>
  );
}
